// =======================
// src/paginas/analise/processos/analiseProcessos.js
// =======================
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  TextField,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Fade,
  Zoom,
  Slide,
  Grow,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery,
  DialogActions,
  Tooltip,
  IconButton,
  Chip,
  keyframes,
  styled,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import SchoolIcon from "@mui/icons-material/School";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CheckIcon from "@mui/icons-material/Check";
import DocumentViewer from "./documentosAnalise";
import { api, fetchDocumentoPdf } from "@api/api";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { ajustarTamanhoFonteAnalista } from "../../../estilos/global";

// Animação do botão
const buttonPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(63, 81, 181, 0.4);
  }
  70% {
    transform: scale(1.02);
    box-shadow: 0 0 0 12px rgba(63, 81, 181, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(63, 81, 181, 0);
  }
`;

// Animação de checkmark
const checkAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Estilo do botão premium
const PremiumButton = styled(Button)(({ theme, ispulse, issuccess }) => ({
  position: "relative",
  overflow: "hidden",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: ajustarTamanhoFonteAnalista("1rem"),
  padding: "12px 24px",
  borderRadius: "8px",
  transition: "all 0.3s ease",
  animation: ispulse === "true" ? `${buttonPulse} 2s infinite` : "none",

  background:
    ispulse === "true"
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
      : issuccess === "true"
        ? theme.palette.success.main
        : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,

  color: "white",
  boxShadow:
    ispulse === "true"
      ? "0 4px 20px rgba(63, 81, 181, 0.3)"
      : "0 4px 12px rgba(63, 81, 181, 0.2)",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      ispulse === "true"
        ? "0 6px 24px rgba(63, 81, 181, 0.4)"
        : "0 6px 16px rgba(63, 81, 181, 0.3)",
    background:
      ispulse === "true"
        ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
        : issuccess === "true"
          ? theme.palette.success.dark
          : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  },

  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.2)",
  },

  "& .MuiButton-startIcon": {
    transition: "all 0.3s ease",
    ...(issuccess === "true" && {
      animation: `${checkAnimation} 0.5s ease-out`,
    }),
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
    transform: "translateX(-100%)",
    transition: "transform 0.6s ease",
  },

  "&:hover::after": {
    transform: "translateX(100%)",
  },

  "&.Mui-disabled": {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: "none",
    transform: "none !important",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  height: "93vh",
  padding: 20,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: theme.shadows[4],
  borderRadius: "12px",
  backgroundColor: "white",
}));

const StatusBadge = styled(Box)(({ status, theme }) => ({
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: "20px",
  fontWeight: "bold",
  backgroundColor:
    status === "Aprovado"
      ? theme.palette.success.light
      : status === "Recusado"
        ? theme.palette.error.light
        : theme.palette.warning.light,
  color:
    status === "Aprovado"
      ? theme.palette.success.contrastText
      : status === "Recusado"
        ? theme.palette.error.contrastText
        : theme.palette.warning.contrastText,
  margin: "0 auto",
}));

const StyledInputField = styled(TextField)(({ theme, disabled }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: disabled
        ? theme.palette.grey[400]
        : theme.palette.primary.main,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: theme.palette.text.primary,
  },
}));

const applySeiMask = (value) => {
  const numbers = value.replace(/\D/g, "");
  let masked = "";

  if (numbers.length > 0) masked += numbers.substring(0, 4);
  if (numbers.length > 4) masked += `.${numbers.substring(4, 6)}`;
  if (numbers.length > 6) masked += `.${numbers.substring(6, 13)}`;
  if (numbers.length > 13) masked += `/${numbers.substring(13, 17)}`;
  if (numbers.length > 17) masked += `-${numbers.substring(17, 19)}`;

  return masked;
};

const validateSeiFormat = (sei) => {
  const pattern = /^\d{4}\.\d{2}\.\d{7}\/\d{4}-\d{2}$/;
  return pattern.test(sei);
};

const AnalistaPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [numeroSei, setNumeroSei] = useState("");
  const [numeroPortaria, setNumeroPortaria] = useState("");
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [isSeiValid, setIsSeiValid] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchData = async () => {
    try {
      const response = await fetch(api.analystProcesses(id), {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Erro ao carregar dados");
      const data = await response.json();
      setData(data);

      if (data.numero_sei) {
        setNumeroSei(applySeiMask(data.numero_sei));
        setIsSeiValid(validateSeiFormat(applySeiMask(data.numero_sei)));
      } else {
        setNumeroSei("");
        setIsSeiValid(false);
      }

      setNumeroPortaria(data.numero_portaria || "");
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      showAlert("Erro ao carregar dados", "error");
    }
  };

  const handleRejectProcess = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja rejeitar este processo?, o processo sera permanentemente excluido do sistema",
      )
    )
      return;

    try {
      const response = await fetch(api.RecusarProcesso(id), {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao rejeitar processo");
      }

      showAlert("Processo rejeitado com sucesso!", "success");

      setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (error) {
      console.error("Erro:", error);
      showAlert(error.message, "error");
    }
  };

  const { key: chaveNavegacao } = useLocation();

  useEffect(() => {
    setData(null);
  }, [id, chaveNavegacao]);

  useEffect(() => {
    fetchData();
  }, [id, refreshKey, chaveNavegacao]);

  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
    setTimeout(() => {
      setAlert((prev) => ({ ...prev, open: false }));
    }, 5000);
  };

  const isProcessCompleted = data?.concluida === true;
  const isAssociated = data?.associado === true;

  const handleSaveSei = async () => {
    if (!validateSeiFormat(numeroSei)) {
      showAlert(
        "O número SEI deve estar no formato XXXX.XX.XXXXXXX/XXXX-XX",
        "error",
      );
      return;
    }

    try {
      const numeroSeiSemMascara = numeroSei.replace(/\D/g, "");

      const response = await fetch(api.analystSei(id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numero_sei: numeroSeiSemMascara }),
        credentials: "include",
      });

      if (!response.ok) throw new Error("Erro ao atualizar SEI");

      showAlert("Número SEI atualizado com sucesso", "success");
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Erro:", error);
      showAlert(error.message, "error");
    }
  };

  const handleSeiChange = (e) => {
    if (isProcessCompleted) return;

    const rawValue = e.target.value.replace(/\D/g, "");
    let maskedValue = "";

    if (rawValue.length > 0) maskedValue = rawValue.substring(0, 4);
    if (rawValue.length > 4) maskedValue += `.${rawValue.substring(4, 6)}`;
    if (rawValue.length > 6) maskedValue += `.${rawValue.substring(6, 13)}`;
    if (rawValue.length > 13) maskedValue += `/${rawValue.substring(13, 17)}`;
    if (rawValue.length > 17) maskedValue += `-${rawValue.substring(17, 19)}`;

    setNumeroSei(maskedValue);
    setIsSeiValid(validateSeiFormat(maskedValue));
  };

  const handleAssignAnalyst = async () => {
    setIsAssigning(true);

    try {
      const response = await fetch(api.AssociateProcessAna(id), {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao atribuir analista");
      }

      setIsSuccess(true);
      showAlert("Processo atribuído com sucesso!", "success");

      setTimeout(() => {
        setIsSuccess(false);
        setRefreshKey((prev) => prev + 1);
      }, 2000);
    } catch (error) {
      console.error("Erro:", error);
      showAlert(error.message, "error");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassignAnalyst = async () => {
    const confirmed = window.confirm(
      "Deseja desatribuir o analista de todos os processos abertos desta escola?",
    );

    if (!confirmed) return;

    setIsUnassigning(true);

    try {
      const response = await fetch(api.DisassociateProcessAna(id), {
        method: "PATCH",
        credentials: "include",
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Erro ao desatribuir analista");
      }

      showAlert(responseData.message, "success");
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Erro:", error);
      showAlert(error.message, "error");
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleSavePortaria = async (confirmado = false) => {
    try {
      const response = await fetch(api.analystPortaria(id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          numero_portaria: numeroPortaria,
          confirmado: confirmado,
        }),
      });

      if (!response.ok) throw new Error("Erro ao atualizar Portaria");

      showAlert("Número Portaria atualizado com sucesso", "success");
      setRefreshKey((prev) => prev + 1);
      setConfirmDialog(false);
    } catch (error) {
      console.error("Erro:", error);
      showAlert(error.message, "error");
      setConfirmDialog(false);
    }
  };

  const handleDownloadAll = async () => {
    const documentosDisponiveis =
      data?.documentos?.filter((documento) => documento.id) || [];

    if (documentosDisponiveis.length === 0 || isDownloadingAll) return;

    setIsDownloadingAll(true);
    setDownloadStatus(`Preparando 0 de ${documentosDisponiveis.length}...`);

    try {
      const zip = new JSZip();
      let concluidos = 0;

      await Promise.all(
        documentosDisponiveis.map(async (documento) => {
          const blob = await fetchDocumentoPdf(
            api.analystDocumentoConteudo(documento.id),
          );

          const nomeDocumento = documento.nome_documento || "documento";

          zip.file(`${nomeDocumento}.pdf`, blob);

          concluidos += 1;
          setDownloadStatus(
            `Preparando ${concluidos} de ${documentosDisponiveis.length}...`,
          );
        }),
      );

      const content = await zip.generateAsync(
        { type: "blob" },
        ({ percent }) => {
          setDownloadStatus(`Gerando ZIP: ${Math.round(percent)}%`);
        },
      );

      saveAs(content, `${data.nome_escola}.zip`);
    } catch (error) {
      console.error("Erro ao baixar documentos:", error);
      showAlert(
        error.message || "Não foi possível baixar os documentos",
        "error",
      );
    } finally {
      setIsDownloadingAll(false);
      setDownloadStatus("");
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!data)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="white"
      >
        <Fade in={true}>
          <Typography variant="h6" color="textSecondary">
            Carregando dados...
          </Typography>
        </Fade>
      </Box>
    );

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "white",
        padding: 0,
      }}
    >
      <Slide direction="up" in={true} mountOnEnter unmountOnExit>
        <StyledCard>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              padding: "16px",
            }}
          >
            {alert.open && (
              <Grow in={alert.open}>
                <Alert
                  severity={alert.severity}
                  onClose={() => setAlert({ ...alert, open: false })}
                  sx={{ mb: 2 }}
                >
                  {alert.message}
                </Alert>
              </Grow>
            )}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Box
                textAlign="center"
                flex={1}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Fade in={true} timeout={800}>
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                      fontSize: isMobile
                        ? ajustarTamanhoFonteAnalista("1.5rem")
                        : ajustarTamanhoFonteAnalista("2rem"),
                      mr: 2,
                    }}
                  >
                    Análise de Documento
                  </Typography>
                </Fade>

                {!isProcessCompleted && (
                  <Grow in={true} timeout={1200}>
                    <Box display="flex" gap={2}>
                      {isAssociated ? (
                        <PremiumButton
                          variant="contained"
                          startIcon={<PersonRemoveIcon />}
                          onClick={handleUnassignAnalyst}
                          disabled={isUnassigning}
                          ispulse="false"
                          issuccess="false"
                          sx={{
                            minWidth: "220px",
                            height: "48px",
                            background: theme.palette.warning.main,
                            "&:hover": {
                              background: theme.palette.warning.dark,
                            },
                          }}
                        >
                          {isUnassigning ? "Desatribuindo..." : "Desatribuir"}
                        </PremiumButton>
                      ) : (
                        <PremiumButton
                          variant="contained"
                          startIcon={
                            isSuccess ? <CheckIcon /> : <GroupAddIcon />
                          }
                          onClick={handleAssignAnalyst}
                          disabled={isAssigning}
                          ispulse={(!isAssigning && !isSuccess).toString()}
                          issuccess={isSuccess.toString()}
                          sx={{
                            minWidth: "220px",
                            height: "48px",
                          }}
                        >
                          {isAssigning
                            ? "Atribuindo..."
                            : isSuccess
                              ? "Atribuído!"
                              : "Atribuir a Mim"}
                        </PremiumButton>
                      )}

                      <PremiumButton
                        variant="contained"
                        color="error"
                        startIcon={<WarningIcon />}
                        onClick={handleRejectProcess}
                        ispulse="false"
                        issuccess="false"
                        sx={{ minWidth: "180px", height: "48px" }}
                      >
                        Rejeitar Processo
                      </PremiumButton>
                    </Box>
                  </Grow>
                )}
              </Box>
              <Box width={40} />
            </Box>

            <Grow in={true} timeout={1000}>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <StatusBadge status={data.status}>{data.status}</StatusBadge>
              </Box>
            </Grow>

            {data.analista_nome && (
              <Grow in={true} timeout={1200}>
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Chip
                    icon={<PersonIcon />}
                    label={`Analista: ${data.analista_nome}`}
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.light + "20",
                      fontSize: ajustarTamanhoFonteAnalista("0.9rem"),
                      padding: "8px 12px",
                    }}
                  />
                </Box>
              </Grow>
            )}

            <Grow in={true} timeout={1400}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  mb: 2,
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: "200px",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      mr: 1,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <SchoolIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Escola
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: ajustarTamanhoFonteAnalista("0.9rem") }}
                    >
                      {data.nome_escola}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: "200px",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.secondary.main,
                      mr: 1,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Data
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: ajustarTamanhoFonteAnalista("0.9rem") }}
                    >
                      {new Date(data.data_criacao).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: "200px",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.info.main,
                      mr: 1,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <LocationCityIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Município
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: ajustarTamanhoFonteAnalista("0.9rem") }}
                    >
                      {data.municipio}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grow>

            <Grow in={true} timeout={1600}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  mb={0.5}
                >
                  <DescriptionIcon
                    color="primary"
                    sx={{ mr: 1, fontSize: "1rem" }}
                  />
                  Tipo de Processo
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.4,
                    fontSize: ajustarTamanhoFonteAnalista("0.875rem"),
                  }}
                >
                  {data.processo_nome || "Tipo de processo não informado"}
                </Typography>
                {data.processo_nome?.length > 100 && (
                  <Box textAlign="right" mt={0.5}>
                    <Button
                      size="small"
                      onClick={() => setOpen(true)}
                      sx={{
                        textTransform: "none",
                        fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                      }}
                      variant="text"
                      color="primary"
                    >
                      Ver nome completo
                    </Button>
                  </Box>
                )}
              </Box>
            </Grow>

            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                  py: 1.5,
                }}
              >
                <Typography variant="subtitle1">Tipo de processo</Typography>
              </DialogTitle>
              <DialogContent sx={{ p: 2 }}>
                <Typography
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.6,
                    fontSize: ajustarTamanhoFonteAnalista("0.875rem"),
                  }}
                >
                  {data.processo_nome || "Tipo de processo não informado"}
                </Typography>
              </DialogContent>
            </Dialog>

            <Grow in={true} timeout={1800}>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      align="center"
                      fontWeight="bold"
                      mb={0.5}
                    >
                      Número SEI
                      {!isProcessCompleted && (
                        <Tooltip title="Formato: XXXX.XX.XXXXXXX/XXXX-XX" arrow>
                          <InfoIcon
                            color="action"
                            sx={{ fontSize: "1rem", ml: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StyledInputField
                        fullWidth
                        value={numeroSei}
                        onChange={handleSeiChange}
                        variant="outlined"
                        size="small"
                        placeholder="XXXX.XX.XXXXXXX/XXXX-XX"
                        inputProps={{
                          maxLength: 25,
                          readOnly: isProcessCompleted,
                        }}
                        error={
                          !isProcessCompleted &&
                          numeroSei.length > 0 &&
                          !isSeiValid
                        }
                        helperText={
                          !isProcessCompleted &&
                          numeroSei.length > 0 &&
                          !isSeiValid &&
                          "Formato esperado: XXXX.XX.XXXXXXX/XXXX-XX"
                        }
                        disabled={isProcessCompleted}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={
                          !isProcessCompleted ? handleSaveSei : undefined
                        }
                        disabled={isProcessCompleted || !isSeiValid}
                        sx={{
                          minWidth: "36px",
                          height: "36px",
                          padding: "6px",
                          "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                          },
                        }}
                      >
                        <SaveIcon />
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      align="center"
                      fontWeight="bold"
                      mb={0.5}
                    >
                      Número Portaria
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StyledInputField
                        fullWidth
                        value={numeroPortaria}
                        onChange={
                          !isProcessCompleted
                            ? (e) => setNumeroPortaria(e.target.value)
                            : undefined
                        }
                        variant="outlined"
                        size="small"
                        placeholder="Número Portaria"
                        inputProps={{
                          readOnly: isProcessCompleted,
                        }}
                        disabled={isProcessCompleted}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={
                          !isProcessCompleted
                            ? () => setConfirmDialog(true)
                            : undefined
                        }
                        disabled={isProcessCompleted}
                        sx={{
                          minWidth: "36px",
                          height: "36px",
                          padding: "6px",
                          "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                          },
                        }}
                      >
                        <SaveIcon />
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grow>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: "50%",
              }}
            >
              <Grow in={true} timeout={2000}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    display="flex"
                    alignItems="center"
                    sx={{ fontSize: ajustarTamanhoFonteAnalista("1.1rem") }}
                  >
                    <DescriptionIcon
                      color="primary"
                      sx={{ mr: 1, fontSize: "1rem" }}
                    />
                    Documentos
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleDownloadAll}
                      startIcon={
                        isDownloadingAll ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <DownloadIcon sx={{ fontSize: "1rem" }} />
                        )
                      }
                      disabled={
                        isDownloadingAll ||
                        !data.documentos ||
                        data.documentos.length === 0
                      }
                      sx={{
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                        whiteSpace: "nowrap",
                        "& .MuiButton-startIcon": {
                          marginRight: "4px",
                        },
                      }}
                    >
                      {isDownloadingAll ? downloadStatus : "Baixar Todos"}
                    </Button>

                    {isDownloadingAll && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        aria-live="polite"
                      >
                        Mantenha esta página aberta até o download começar.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grow>

              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "#f9f9f9",
                  p: 1,
                }}
              >
                <DocumentViewer
                  documentos={data.documentos}
                  refreshParent={() => setRefreshKey((prev) => prev + 1)}
                />
              </Box>
            </Box>

            {!isProcessCompleted && (
              <Dialog
                open={confirmDialog}
                onClose={() => setConfirmDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                  sx: {
                    borderRadius: "12px",
                    overflow: "hidden",
                    minWidth: isMobile ? "90%" : "400px",
                    background: theme.palette.background.paper,
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
                  },
                }}
              >
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    p: 3,
                    textAlign: "center",
                  }}
                >
                  <SaveIcon sx={{ fontSize: "2.5rem", mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Confirmar Conclusão do Processo
                  </Typography>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    Você está prestes a concluir o processo com a portaria:
                  </Typography>

                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: "8px",
                      p: 2,
                      mb: 3,
                      textAlign: "center",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.primary.dark,
                      }}
                    >
                      {numeroPortaria || "(Nenhum número informado)"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: theme.palette.warning.light,
                      p: 2,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <WarningIcon sx={{ color: theme.palette.warning.dark }} />
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.warning.dark }}
                    >
                      Após a confirmação, o processo será marcado como concluído
                      e não poderá ser alterado.
                    </Typography>
                  </Box>
                </DialogContent>

                <DialogActions
                  sx={{
                    p: 2,
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  <Button
                    onClick={() => setConfirmDialog(false)}
                    variant="outlined"
                    sx={{
                      borderRadius: "6px",
                      px: 3,
                      py: 1,
                      fontWeight: "bold",
                      borderColor: theme.palette.grey[400],
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        backgroundColor: theme.palette.grey[100],
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => handleSavePortaria(true)}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: "6px",
                      px: 3,
                      py: 1,
                      fontWeight: "bold",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: "none",
                      },
                    }}
                  >
                    Confirmar
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </CardContent>
        </StyledCard>
      </Slide>
    </Container>
  );
};

export default AnalistaPage;
